import { useEffect, useState } from 'react';

/**
 * Tracks the live pixel width of a ref'd element via ResizeObserver.
 *
 * Used to switch the fill chrome between the desktop side-rail layout and
 * the single-column fallback based on the *card's own width* rather than a
 * viewport media query — so a narrow modal or a wide full-page filler each
 * pick the right layout regardless of window size, and the switch survives
 * whatever Tailwind purge each consuming app runs (no @container utility to
 * lose). SSR-safe: returns 0 until the first measurement.
 */
export function useElementWidth<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    // Seed synchronously so the first paint already knows the layout.
    setWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // borderBoxSize is the reliable cross-browser width; fall back to
        // the bounding rect where the array form isn't populated.
        const box = entry.borderBoxSize?.[0];
        setWidth(box ? box.inlineSize : entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return width;
}
